import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { notificar } from '../utils/notify';

// Ocorrências das APIs do BPO RH — mesma ideia da tela do webhook da Omie:
// ver o que as integrações receberam e executaram sem abrir o banco.
// Duas fontes: os eventos que a Convenia manda (webhook) e as execuções de
// sincronização (o que o Saber foi buscar lá).

const FONTES = [
  { id: 'webhook', rotulo: 'Webhooks da Convenia', icone: 'fa-tower-broadcast',
    ajuda: 'Cada evento que a Convenia enviou para o Saber (admissão, férias, edição de colaborador...).' },
  { id: 'sync', rotulo: 'Sincronizações', icone: 'fa-rotate',
    ajuda: 'Cada execução de sincronização com a Convenia, por entidade.' },
  { id: 'chamada', rotulo: 'Envios à Convenia', icone: 'fa-paper-plane',
    ajuda: 'O que o Saber MANDOU para a Convenia (admissão, desligamento) — com o payload e a resposta, inclusive o motivo de uma recusa.' },
  { id: 'ferias', rotulo: 'Férias (job 2 em 2 h)', icone: 'fa-umbrella-beach',
    ajuda: 'Cada mudança que o job de férias fez no espelho: solicitação ou período inserido, atualizado (com o de-para) ou removido porque a Convenia excluiu.' },
];

const STATUS = {
  ok: { bg: '#e6f7ef', cor: '#116b47', borda: '#a8dcc3', rotulo: 'ok', icone: 'fa-circle-check' },
  erro: { bg: '#fdeaec', cor: '#a11d2b', borda: '#f3b8bf', rotulo: 'erro', icone: 'fa-circle-exclamation' },
  ignorado: { bg: '#eef2f7', cor: '#495057', borda: '#d6dee8', rotulo: 'ignorado', icone: 'fa-circle-minus' },
  // fonte 'ferias': o status é a ação feita no espelho
  inserida: { bg: '#e6f7ef', cor: '#116b47', borda: '#a8dcc3', rotulo: 'inserida', icone: 'fa-circle-plus' },
  atualizada: { bg: '#e8f1fb', cor: '#0a4f8a', borda: '#b7d3ee', rotulo: 'atualizada', icone: 'fa-pen-to-square' },
  removida: { bg: '#fdeaec', cor: '#a11d2b', borda: '#f3b8bf', rotulo: 'removida', icone: 'fa-circle-minus' },
};

// cartões do resumo por fonte
const CHAVES_RESUMO = { ferias: ['inserida', 'atualizada', 'removida'] };
const chavesResumo = (fonte) => CHAVES_RESUMO[fonte] || ['ok', 'erro', 'ignorado'];

const FILTROS_INICIAIS = { cliente_id: '', tipo: '', status: '', busca: '', dias: '7' };

const ApiOcorrencias = () => {
  const API = process.env.REACT_APP_API_BASE;
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const [fonte, setFonte] = useState('webhook');
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [page, setPage] = useState(1);
  const [detalhe, setDetalhe] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/bporh/api-ocorrencias/`, {
      params: { fonte, page, page_size: 50, ...filtros },
      headers: headers(),
    })
      .then((res) => setDados(res.data))
      .catch(() => { setDados(null); setMensagem('Não foi possível carregar as ocorrências'); })
      .finally(() => setCarregando(false));
  }, [fonte, page, filtros]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [carregar]);

  // trocar de fonte zera filtros de tipo (as listas são diferentes) e a página
  const trocarFonte = (id) => {
    setFonte(id);
    setPage(1);
    setFiltros(f => ({ ...f, tipo: '' }));
  };

  const setF = (campo) => (e) => {
    setPage(1);
    setFiltros(f => ({ ...f, [campo]: e.target.value }));
  };

  const abrirDetalhe = (item) => {
    setDetalhe({ carregando: true });
    axios.get(`${API}/api/bporh/api-ocorrencias/${item.id}/`, {
      params: { fonte }, headers: headers(),
    })
      .then((res) => setDetalhe({ item, ...res.data.detalhe }))
      .catch(() => { setDetalhe(null); setMensagem('Não foi possível abrir o detalhe'); });
  };

  const resumo = dados?.resumo || {};
  const totalPaginas = Math.max(1, Math.ceil((dados?.total || 0) / (dados?.page_size || 50)));
  const fonteAtual = FONTES.find(f => f.id === fonte);
  const ehFerias = fonte === 'ferias';
  const colunas = fonte === 'sync' ? 8 : ehFerias ? 7 : 6;

  return (
    <div className="container-fluid px-4 py-3">
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body d-flex align-items-center flex-wrap gap-3">
          <i className="fas fa-diagram-project header-icon me-2"></i>
          <h2 className="titulo-pagina m-0">Ocorrências das APIs — BPO RH</h2>
        </div>
      </div>

      {/* fontes */}
      <div className="btn-group mb-3">
        {FONTES.map(f => (
          <button key={f.id}
            className={`btn btn-sm ${fonte === f.id ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => trocarFonte(f.id)} title={f.ajuda}>
            <i className={`fas ${f.icone} me-2`}></i>{f.rotulo}
          </button>
        ))}
      </div>

      {/* resumo do período */}
      <div className="row g-2 mb-3">
        {chavesResumo(fonte).map(k => {
          const e = STATUS[k];
          return (
            <div className="col-6 col-md-3" key={k}>
              <button className="card border-0 shadow-sm w-100 text-start"
                style={{ background: e.bg, cursor: 'pointer' }}
                onClick={() => { setPage(1); setFiltros(f => ({ ...f, status: f.status === k ? '' : k })); }}
                title={`Filtrar por ${e.rotulo}`}>
                <div className="card-body py-2">
                  <div className="small" style={{ color: e.cor, fontWeight: 700 }}>
                    <i className={`fas ${e.icone} me-2`}></i>{e.rotulo}
                    {filtros.status === k && <i className="fas fa-filter ms-2"></i>}
                  </div>
                  <div className="fs-4 fw-bold" style={{ color: e.cor }}>{resumo[k] ?? 0}</div>
                </div>
              </button>
            </div>
          );
        })}
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body py-2">
              <div className="small text-muted fw-bold">
                <i className="fas fa-list me-2"></i>no filtro
              </div>
              <div className="fs-4 fw-bold" style={{ color: '#00205b' }}>{dados?.total ?? 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* filtros */}
      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <select className="form-select form-select-sm" value={filtros.cliente_id}
            onChange={setF('cliente_id')}>
            <option value="">Todas as empresas</option>
            {(dados?.clientes || []).map(c => (
              <option key={c.id} value={c.id}>{c.cliente}</option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <select className="form-select form-select-sm" value={filtros.tipo} onChange={setF('tipo')}>
            <option value="">
              {fonte === 'sync' ? 'Todas as entidades'
                : fonte === 'chamada' ? 'Todas as operações'
                  : ehFerias ? 'Solicitações e períodos' : 'Todos os eventos'}
            </option>
            {(dados?.tipos || []).map(t => (
              <option key={t.tipo} value={t.tipo}>{t.rotulo || t.tipo} ({t.n})</option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={filtros.dias} onChange={setF('dias')}>
            <option value="1">Hoje</option>
            <option value="7">7 dias</option>
            <option value="30">30 dias</option>
            <option value="90">90 dias</option>
            <option value="3650">Todo o período</option>
          </select>
        </div>
        <div className="col-md-3">
          <input type="text" className="form-control form-control-sm"
            placeholder="Buscar no conteúdo..." value={filtros.busca} onChange={setF('busca')} />
        </div>
        <div className="col-md-1">
          <button className="btn btn-outline-primary btn-sm w-100" onClick={carregar}
            disabled={carregando} title="Atualizar">
            <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`}></i>
          </button>
        </div>
      </div>

      <div className="table-responsive bg-white rounded shadow-sm">
        <table className="table table-striped table-hover align-middle mb-0">
          <thead style={{ background: '#f8f9fa' }}>
            <tr>
              <th><i className="fas fa-clock me-2"></i>Quando</th>
              <th><i className="fas fa-building me-2"></i>Empresa</th>
              {ehFerias && <th><i className="fas fa-user me-2"></i>Colaborador</th>}
              <th><i className="fas fa-tag me-2"></i>
                {fonte === 'sync' ? 'Entidade' : fonte === 'chamada' ? 'Operação'
                  : ehFerias ? 'Registro' : 'Evento'}</th>
              <th><i className="fas fa-flag me-2"></i>{ehFerias ? 'Ação' : 'Status'}</th>
              {fonte === 'sync' && <th className="text-end">Registros</th>}
              {fonte === 'sync' && <th className="text-end">Duração</th>}
              <th>Detalhe</th>
              <th className="text-center">Ver</th>
            </tr>
          </thead>
          <tbody>
            {(dados?.itens || []).map(it => {
              const e = STATUS[it.status] || STATUS.ignorado;
              return (
                <tr key={it.id} style={{ cursor: 'pointer' }} onClick={() => abrirDetalhe(it)}>
                  <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {it.quando}
                  </td>
                  <td>{it.cliente}</td>
                  {ehFerias && <td>{it.colaborador}</td>}
                  <td>{ehFerias
                    ? <span className="small">{it.tipo}</span>
                    : <code style={{ color: '#00205b' }}>{it.tipo}</code>}</td>
                  <td>
                    <span className="badge" style={{
                      background: e.bg, color: e.cor, border: `1px solid ${e.borda}`, fontWeight: 600,
                    }}>
                      <i className={`fas ${e.icone} me-1`}></i>{e.rotulo}
                    </span>
                  </td>
                  {fonte === 'sync' && (
                    <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {it.registros ?? '—'}
                    </td>
                  )}
                  {fonte === 'sync' && (
                    <td className="text-end text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {it.duracao_seg != null ? `${it.duracao_seg}s` : '—'}
                    </td>
                  )}
                  <td className="text-muted small" style={{ maxWidth: ehFerias ? '460px' : '320px' }}>
                    {(it.detalhe || '').slice(0, ehFerias ? 200 : 120) || '—'}
                  </td>
                  <td className="text-center">
                    <button className="btn btn-sm btn-outline-primary py-0 px-2"
                      onClick={(ev) => { ev.stopPropagation(); abrirDetalhe(it); }}>
                      <i className="fas fa-eye"></i>
                    </button>
                  </td>
                </tr>
              );
            })}
            {!carregando && !(dados?.itens || []).length && (
              <tr>
                <td colSpan={colunas} className="text-center text-muted py-4">
                  <i className="fas fa-inbox me-2"></i>
                  Nenhuma ocorrência de {fonteAtual?.rotulo.toLowerCase()} nestes filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPaginas > 1 && (
        <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
          <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <span className="small text-muted">página {page} de {totalPaginas}</span>
          <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPaginas}
            onClick={() => setPage(p => p + 1)}>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

      {/* ─── MODAL: detalhe da ocorrência ─── */}
      {detalhe && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, overflowY: 'auto' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className={`fas ${ehFerias ? 'fa-umbrella-beach' : 'fa-code'} me-2`}></i>
                    {ehFerias
                      ? `${detalhe.tabela || 'Férias'} ${detalhe.acao || ''}`.trim()
                      : (detalhe.tipo_evento || detalhe.entidade || 'Ocorrência')}
                  </h5>
                  <small className="text-muted">
                    {detalhe.quando || detalhe.iniciado_em}
                    {detalhe.item?.cliente && ` · ${detalhe.item.cliente}`}
                    {ehFerias && detalhe.colaborador && ` · ${detalhe.colaborador}`}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setDetalhe(null)}></button>
              </div>
              <div className="modal-body">
                {detalhe.carregando ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary"></div>
                  </div>
                ) : fonte === 'chamada' ? (
                  <>
                    <div className="row g-2 mb-3 small">
                      <div className="col-6"><b>Operação:</b> {detalhe.operacao}</div>
                      <div className="col-6">
                        <b>HTTP:</b>{' '}
                        <span className={detalhe.status_http >= 400 ? 'text-danger fw-bold' : 'text-success'}>
                          {detalhe.status_http}
                        </span>
                      </div>
                      {detalhe.contexto && (
                        <div className="col-12"><b>Contexto:</b> {detalhe.contexto}</div>
                      )}
                    </div>
                    <div className="small fw-bold text-muted mb-1">Resposta da Convenia</div>
                    <pre className="bg-light border rounded p-3 small"
                      style={{ maxHeight: '300px', overflow: 'auto' }}>
                      {JSON.stringify(detalhe.resposta, null, 2)}
                    </pre>
                    <div className="small fw-bold text-muted mb-1">Payload enviado</div>
                    <pre className="bg-light border rounded p-3 small mb-0"
                      style={{ maxHeight: '300px', overflow: 'auto' }}>
                      {JSON.stringify(detalhe.payload, null, 2)}
                    </pre>
                  </>
                ) : ehFerias ? (
                  <>
                    <div className="row g-2 mb-3 small">
                      <div className="col-12">
                        <b>O que aconteceu:</b> {detalhe.resumo}
                      </div>
                      <div className="col-6"><b>Ação:</b>{' '}
                        {(() => { const e = STATUS[detalhe.acao] || STATUS.ignorado; return (
                          <span className="badge" style={{ background: e.bg, color: e.cor, border: `1px solid ${e.borda}`, fontWeight: 600 }}>
                            <i className={`fas ${e.icone} me-1`}></i>{e.rotulo}
                          </span>); })()}
                      </div>
                      <div className="col-6"><b>Origem:</b> {detalhe.origem} · rodada {detalhe.execucao}</div>
                    </div>
                    {detalhe.alteracoes && Object.keys(detalhe.alteracoes).length > 0 && (
                      <>
                        <div className="small fw-bold text-muted mb-1">Campos alterados</div>
                        <table className="table table-sm table-bordered small mb-3">
                          <thead style={{ background: '#f8f9fa' }}>
                            <tr><th>Campo</th><th>Antes</th><th>Depois</th></tr>
                          </thead>
                          <tbody>
                            {Object.entries(detalhe.alteracoes).map(([campo, [antes, depois]]) => (
                              <tr key={campo}>
                                <td><code>{campo}</code></td>
                                <td className="text-muted">{antes === null || antes === undefined ? '—' : String(antes)}</td>
                                <td className="fw-bold">{depois === null || depois === undefined ? '—' : String(depois)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                    <div className="small fw-bold text-muted mb-1">
                      {detalhe.acao === 'removida' ? 'Registro como estava no espelho' : 'Registro gravado'}
                    </div>
                    <pre className="bg-light border rounded p-3 small mb-0"
                      style={{ maxHeight: '300px', overflow: 'auto' }}>
                      {JSON.stringify(detalhe.dados, null, 2)}
                    </pre>
                  </>
                ) : fonte === 'sync' ? (
                  <>
                    <div className="row g-2 mb-3 small">
                      <div className="col-6"><b>Início:</b> {detalhe.iniciado_em || '—'}</div>
                      <div className="col-6"><b>Fim:</b> {detalhe.finalizado_em || '—'}</div>
                      <div className="col-6"><b>Registros:</b> {detalhe.registros ?? '—'}</div>
                      <div className="col-6"><b>Status:</b> {detalhe.status}</div>
                    </div>
                    <div className="small fw-bold text-muted mb-1">Detalhe</div>
                    <pre className="bg-light border rounded p-3 small mb-0"
                      style={{ whiteSpace: 'pre-wrap' }}>{detalhe.texto || '(vazio)'}</pre>
                  </>
                ) : (
                  <>
                    <div className="row g-2 mb-3 small">
                      <div className="col-6">
                        <b>Assinatura:</b>{' '}
                        {detalhe.assinatura_valida
                          ? <span className="text-success">válida</span>
                          : <span className="text-danger">inválida</span>}
                      </div>
                      <div className="col-6">
                        <b>Processado:</b> {detalhe.processado ? 'sim' : 'não'}
                      </div>
                      {detalhe.resultado && (
                        <div className="col-12"><b>Resultado:</b> {detalhe.resultado}</div>
                      )}
                    </div>
                    <div className="small fw-bold text-muted mb-1">Payload</div>
                    <pre className="bg-light border rounded p-3 small"
                      style={{ maxHeight: '340px', overflow: 'auto' }}>
                      {JSON.stringify(detalhe.payload, null, 2)}
                    </pre>
                    <div className="small fw-bold text-muted mb-1">Headers</div>
                    <pre className="bg-light border rounded p-3 small mb-0"
                      style={{ maxHeight: '180px', overflow: 'auto' }}>
                      {JSON.stringify(detalhe.headers, null, 2)}
                    </pre>
                  </>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setDetalhe(null)}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiOcorrencias;
